"use client";

import { useEffect, useMemo, useState, type JSX } from "react";
import type { PostHog } from "posthog-js";
// Important: global-error replaces your root layout, so import globals here if you want consistent styling.
import "./globals.css";
import Link from "next/link";
import { loadPosthog } from "../../instrumentation-client";
import { decodeObfuscatedEmail } from "@/lib/obfuscated-email";

// Encoded so the address never sits in the repository in plaintext; see
// adr/0011-serve-the-contact-email-behind-a-redirect.md.
const ENCODED_BUG_REPORT_EMAIL = "nvsjzkv at wdrikze dot ty";

type GlobalErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

/**
 * Kept outside the component so the impure calls stay out of render. The
 * component runs this once through a lazy useState initializer, so the ID is
 * generated a single time and stays stable across re-renders.
 *
 * Correlation ID for error reports, not a security credential. randomUUID is
 * simply the platform's standard unique-ID source.
 */
function createEventId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Only reachable without Web Crypto (ancient browser, plain-http context).
  // A timestamp is enough for a best-effort correlation ID.
  return `err_${Date.now()}`;
}

/** See createEventId: run once via a lazy useState initializer, not in render. */
function createErrorTime(): string {
  return new Date().toISOString();
}

export default function GlobalError({
  error,
  reset,
}: GlobalErrorProps): JSX.Element {
  const [eventId] = useState(createEventId);
  // PostHog lives in state rather than a ref so that `details` below
  // recomputes when it finishes loading. With the previous ref, the render
  // path never saw the loaded client, and the distinct/session IDs shown on
  // screen, in the mailto body, and in the copied payload stayed "unknown"
  // forever even though the captured telemetry had the real values.
  const [posthog, setPosthog] = useState<PostHog | null>(null);
  const [copied, setCopied] = useState(false);

  // Generated once alongside the event ID, so the reported time stays the
  // time of the error rather than the time PostHog finished loading.
  const [time] = useState(createErrorTime);

  const details = useMemo(() => {
    // PostHog IDs (best-effort) - "unknown" until PostHog has loaded
    const distinctId = safe(() => posthog?.get_distinct_id?.()) ?? "unknown";
    const sessionId = safe(() => posthog?.get_session_id?.()) ?? "unknown";

    return {
      eventId,
      message: error?.message ?? "Unknown error",
      name: error?.name ?? "Error",
      stack: error?.stack ?? "n/a",
      digest: error?.digest ?? "n/a",
      url: safe(() => window.location.href) ?? "n/a",
      userAgent: safe(() => navigator.userAgent) ?? "n/a",
      distinctId,
      sessionId,
      time,
    };
  }, [error, eventId, posthog, time]);

  // Load PostHog and capture error exactly once per render of this error
  useEffect(() => {
    let cancelled = false;
    void loadPosthog()
      .then((loaded) => {
        if (!loaded) return;
        if (!cancelled) {
          setPosthog(loaded);
        }

        const props = {
          error_event_id: details.eventId,
          error_name: details.name,
          error_message: details.message,
          error_stack: details.stack,
          digest: details.digest,
          url: details.url,
          user_agent: details.userAgent,
          distinct_id: safe(() => loaded.get_distinct_id?.()) ?? "unknown",
          $session_id: safe(() => loaded.get_session_id?.()) ?? "unknown",
        };

        loaded.captureException(error, props);
      })
      .catch(() => {
        // never let telemetry crash the error UI
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  // Derived directly from `details`, so the prefilled body picks up the
  // PostHog IDs as soon as they are known, and the link is complete on the
  // very first paint instead of starting out empty.
  const mailtoHref = useMemo(() => {
    // Build the mailto with a prefilled subject + body
    const subject = `Bug report [${details.eventId}]`;
    const bodyLines = [
      "Hi François,",
      "",
      "I hit an error on your site. Here is what happened:",
      "",
      "What I did:",
      "(please describe the steps you took just before the error)",
      "",
      `Event ID: ${details.eventId}`,
      `PostHog distinct_id: ${details.distinctId}`,
      `PostHog session_id: ${details.sessionId}`,
      `URL: ${details.url}`,
      `Time: ${details.time}`,
      `User-Agent: ${details.userAgent}`,
      `Digest: ${details.digest}`,
      "",
      "Optional details for debugging:",
      `Error name: ${details.name}`,
      `Error message: ${details.message}`,
      `Stack: ${details.stack}`,
      "",
      "Thanks!",
    ];
    return `mailto:${decodeObfuscatedEmail(ENCODED_BUG_REPORT_EMAIL)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      bodyLines.join("\n"),
    )}`;
  }, [details]);

  const copyAll = async () => {
    const payload = JSON.stringify(details, null, 2);
    try {
      // False positive at this granularity: web-features marks the whole
      // async-clipboard group limited because of clipboard.read(), but
      // writeText alone has been supported everywhere since 2020, and the
      // catch below degrades gracefully where it is missing.
      // eslint-disable-next-line baseline-js/use-baseline -- see above
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      posthog?.capture("error_details_copied", {
        error_event_id: details.eventId,
      });
    } catch {
      // ignore
    }
  };

  return (
    <html lang="en" suppressHydrationWarning>
      <body
        style={{
          margin: 0,
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji'",
          background: "hsl(0 0% 6%)",
          color: "hsl(0 0% 98%)",
          minHeight: "100dvh",
          display: "grid",
          placeItems: "center",
        }}
      >
        <main
          style={{
            width: "min(720px, 92vw)",
            borderRadius: "16px",
            padding: "24px",
            background: "hsl(0 0% 10%)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: "1.5rem",
              lineHeight: 1.2,
              color: "hsl(0 0% 98%)",
            }}
          >
            Something went wrong
          </h1>
          <p style={{ marginTop: 8, opacity: 0.9 }}>
            Error was logged. You can try again or send me a quick report with
            one click.
          </p>

          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              marginTop: 16,
            }}
          >
            <button
              onClick={() => {
                posthog?.capture("error_retry_clicked", {
                  error_event_id: details.eventId,
                });
                reset();
              }}
              style={btnStyle}
            >
              Try again
            </button>

            <Link
              href="/"
              onClick={() =>
                posthog?.capture("error_go_home_clicked", {
                  error_event_id: details.eventId,
                })
              }
              style={{
                ...btnStyle,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Go home
            </Link>

            <a
              href={mailtoHref}
              onClick={() =>
                posthog?.capture("error_report_email_clicked", {
                  error_event_id: details.eventId,
                })
              }
              style={{
                ...btnStyle,
                background: "hsl(200 100% 58%)",
                color: "hsl(0 0% 6%)",
                textDecoration: "none",
              }}
            >
              Report via email
            </a>

            <button
              onClick={copyAll}
              style={{ ...btnStyle, background: "hsl(0 0% 18%)" }}
            >
              {copied ? "Copied" : "Copy details"}
            </button>
          </div>

          <details
            style={{
              marginTop: 16,
              background: "hsl(0 0% 12%)",
              borderRadius: 12,
              padding: 12,
            }}
          >
            <summary style={{ cursor: "pointer" }}>Technical details</summary>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                marginTop: 12,
                fontSize: "0.9rem",
                background: "hsl(0 0% 8%)",
                padding: 12,
                borderRadius: 8,
                overflowX: "auto",
              }}
            >
              {`event_id: ${details.eventId}
url: ${details.url}
time: ${details.time}
user_agent: ${details.userAgent}
distinct_id: ${details.distinctId}
session_id: ${details.sessionId}
digest: ${details.digest}
name: ${details.name}
message: ${details.message}
stack:
${details.stack}`}
            </pre>
          </details>
        </main>
      </body>
    </html>
  );
}

// Small util to guard optional APIs
function safe<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

const btnStyle: React.CSSProperties = {
  appearance: "none",
  border: "0",
  borderRadius: 12,
  padding: "10px 14px",
  background: "hsl(0 0% 16%)",
  color: "inherit",
  cursor: "pointer",
  fontWeight: 600,
};
